import React, { useEffect, useMemo, useState } from "react";
import Header from "../../../components/common/Header/Header";
import useSpaCommand, { isV1Spa } from "../../../hooks/useSpaCommand";
import { useParams } from "react-router-dom";
import { fetchSpa } from "../../../redux/actions/spa.actions";
import { AppDispatch } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import "./PeakManagementPage.css";
import Toggle from "../../../components/common/Toggle/Toggle";
import EconomyClock from "./components/EconomyClock";
import { SnowflakeIcon, SunIcon } from "../../../components/common/Icons/Icons";
import { normalizeForSend, Season } from "./peakSemantics";
import {
    COMPONENTS,
    DAYS,
    DEFAULT_SETTINGS,
    PEAK_FIELDS,
    Settings,
    applySeason,
    componentField,
    describePeriods,
    detectSeason,
    formatRange,
    hasChanges,
    readPeakFields,
    V1_SNAP_MINUTES,
} from "./economyMode";

const SEASONS: { key: Season; label: string }[] = [
    { key: 'summer', label: 'Summer' },
    { key: 'winter', label: 'Winter' },
];

// How long a saved schedule is shown before falling back to what the spa reports,
// if the spa never echoes it back.
const SAVE_ECHO_TIMEOUT = 15000;

/**
 * Economy Mode (peak management), laid out as on the mobile app: season, the
 * days it runs, a 24-hour ring of the tariff periods, and a card per period.
 *
 * Viewing is read-only. EDIT opens a draft — pins on the ring, day circles and
 * a switch per component become live — and nothing reaches the spa until SAVE:
 * a tariff schedule is a set of interdependent values, so it is sent whole.
 */
const PeakManagementPage = () => {
    const { id } = useParams();
    const dispatch: AppDispatch = useDispatch();
    const { data: spaSettings } = useSelector((state: RootState) => state.settings);
    const { data: spaData } = useSelector((state: RootState) => state.spa);
    const isConnected = useSelector((state: RootState) =>
        state.spa.currentSpaId === id && state.spa.isCurrentSpaConnected
    );
    const isConnecting = useSelector((state: RootState) =>
        state.spa.currentSpaId === id && state.spa.isConnecting
    );

    // Fetch SPA From the DB
    useEffect(() => {
        if (id != null) {
            dispatch(fetchSpa(id));
        }
    }, [dispatch, id]);

    // Live connection + command publisher (V2 or V1 spa):
    const command = useSpaCommand(id ?? "");

    // What the spa reports; until it echoes a save back, show what was saved.
    const reported = useMemo(() => readPeakFields(spaSettings), [spaSettings]);
    const [saved, setSaved] = useState<Settings | null>(null);
    const confirmed = saved ?? reported;

    useEffect(() => {
        if (saved && !hasChanges(saved, reported)) setSaved(null);
    }, [saved, reported]);

    useEffect(() => {
        if (!saved) return;
        const timer = setTimeout(() => setSaved(null), SAVE_ECHO_TIMEOUT);
        return () => clearTimeout(timer);
    }, [saved]);

    // The draft only exists while editing, so settings arriving mid-edit can't
    // move a boundary out from under a drag.
    const [draft, setDraft] = useState<Settings | null>(null);
    const [confirmDiscard, setConfirmDiscard] = useState(false);
    const editing = draft !== null;
    const settings = draft ?? confirmed;
    const dirty = editing && hasChanges(confirmed, draft);
    const disabled = !isConnected;

    const periods = describePeriods(settings);
    const season = detectSeason(settings);
    const byKey = Object.fromEntries(periods.map((p) => [p.key, p]));

    const edit = (updater: (prev: Settings) => Settings) =>
        setDraft((prev) => (prev === null ? prev : updater(prev)));

    const toggleDay = (day: string) => edit((prev) => ({ ...prev, [day]: !prev[day] }));
    const toggleComponent = (field: string) => edit((prev) => ({ ...prev, [field]: Number(prev[field]) > 0 ? 0 : 1 }));
    const changeSeason = (next: Season) => edit((prev) => applySeason(prev, next));
    // Staged like any other edit: Cancel still puts the spa's own schedule back.
    const revert = () => edit((prev) => ({ ...prev, ...DEFAULT_SETTINGS }));

    const cancel = () => {
        if (dirty) setConfirmDiscard(true);
        else setDraft(null);
    };

    const discard = () => {
        setDraft(null);
        setConfirmDiscard(false);
    };

    const save = () => {
        if (!draft) return;
        // Reorder the slots so EndMode times ascend and each Mode matches its
        // role: the firmware relies on that, and a drag can leave them out of order.
        const normalized = normalizeForSend(draft);
        const payload = Object.fromEntries(PEAK_FIELDS.map((k) => [k, normalized[k]]));
        command.updateSettings(payload);
        setSaved(payload);
        setDraft(null);
    };

    const periodCard = (key: 'peak' | 'mid' | 'off', wide = false) => {
        const period = byKey[key];
        const switchable = editing && Boolean(period.prefix);
        return (
            <div className={`eco-period ${key} ${wide ? 'wide' : ''}`}>
                <span className="eco-period-bar" />
                <div className="eco-period-body">
                    <span className="eco-period-label">{period.label}</span>
                    <div className="eco-period-ranges">
                        {period.ranges.length
                            ? period.ranges.map((range) => <span key={range.index}>{formatRange(range)}</span>)
                            : <span className="muted">Not scheduled</span>}
                    </div>
                    {switchable ? (
                        <div className="eco-period-toggles">
                            {COMPONENTS.map((component) => {
                                const field = componentField(key, component.key)!;
                                return (
                                    <div key={component.key} className="eco-toggle-row">
                                        <span>{component.label}</span>
                                        <Toggle
                                            label={`${component.label} during ${period.label}`}
                                            checked={Number(settings[field]) > 0}
                                            onChange={() => toggleComponent(field)}
                                            disabled={disabled}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <span className="eco-period-summary">
                            {period.prefix && period.components.length > 0 ? `Runs: ${period.summary}` : period.summary}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="page eco-page">
            <Header />
            {isConnecting ?
            <div className="eco-loading"><span className="spinner" role="progressbar" aria-label="Loading"/></div>
            :
            <main className={`eco-main ${!isConnected ? 'offline' : ''}`}>
                {!isConnected && <div className="page-notice">Spa is offline</div>}

                <div className="eco-layout">
                    {/* Left: season + the 24-hour ring */}
                    <section className="eco-clock-col">
                        {/* Viewing: the season as a plain label. Editing: the switch. */}
                        {editing ? (
                            <div className="segmented eco-seasons" role="group" aria-label="Season">
                                {SEASONS.map((s) => (
                                    <button
                                        key={s.key}
                                        className={season === s.key ? 'active' : ''}
                                        aria-pressed={season === s.key}
                                        disabled={disabled}
                                        onClick={() => changeSeason(s.key)}
                                    >
                                        {s.label}
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <span className="eco-season-label">
                                {season === 'winter' ? <SnowflakeIcon/> : <SunIcon/>}
                                {season === 'winter' ? 'Winter' : season === 'summer' ? 'Summer' : 'Custom'} Schedule
                            </span>
                        )}

                        <EconomyClock
                            settings={settings}
                            editable={editing && !disabled}
                            onChange={(next) => edit(() => next)}
                            snapMinutes={isV1Spa(spaData) ? V1_SNAP_MINUTES : undefined}
                        />

                        <div className="eco-legend" aria-hidden="true">
                            <span className="peak">On-Peak</span>
                            <span className="mid">Mid-Peak</span>
                            <span className="off">Off-Peak</span>
                        </div>

                        {editing && <p className="eco-hint">Drag the pins on the ring to change the hours.</p>}
                    </section>

                    {/* Right: days, periods, actions */}
                    <section className="eco-side">
                        <div className="eco-block">
                            <span className="eco-section-title">Peak Management Days</span>
                            {/* Viewing: a plain list of the days, active ones marked.
                                Editing: tappable circles. */}
                            {editing ? (
                                <div className="eco-days">
                                    {DAYS.map((day) => {
                                        const on = Boolean(settings[day.key]);
                                        return (
                                            <button
                                                key={day.key}
                                                className={`eco-day ${on ? 'on' : ''}`}
                                                role="checkbox"
                                                aria-checked={on}
                                                aria-label={day.label}
                                                disabled={disabled}
                                                onClick={() => toggleDay(day.key)}
                                            >
                                                {day.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <ul className="eco-day-list" aria-label="Days the schedule runs">
                                    {DAYS.map((day) => {
                                        const on = Boolean(settings[day.key]);
                                        return (
                                            <li key={day.key} className={on ? 'on' : ''} aria-label={`${day.label}${on ? '' : ' (off)'}`}>
                                                {day.label}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>

                        <div className="eco-block">
                            <span className="eco-section-title">Peak Management Hours</span>
                            <div className="eco-periods">
                                {periodCard('peak')}
                                {periodCard('mid')}
                                {periodCard('off', true)}
                            </div>
                        </div>

                        {editing ? (
                            <div className="eco-actions">
                                <button className="btn btn-ghost eco-revert" onClick={revert} disabled={disabled}>
                                    Revert to Default
                                </button>
                                <div className="eco-actions-main">
                                    <button className="btn" onClick={cancel}>Cancel</button>
                                    {/* Nothing changed means nothing to write */}
                                    <button className="btn btn-primary" onClick={save} disabled={!dirty || disabled}>Save</button>
                                </div>
                            </div>
                        ) : (
                            <div className="eco-actions">
                                <button className="btn btn-primary eco-edit" onClick={() => setDraft({ ...confirmed })} disabled={disabled}>
                                    Edit
                                </button>
                            </div>
                        )}
                    </section>
                </div>
            </main>
            }

            {confirmDiscard && (
                <div className="modal-overlay" onClick={() => setConfirmDiscard(false)}>
                    <div className="modal" role="alertdialog" aria-modal="true" aria-labelledby="eco-discard-title" onClick={(e) => e.stopPropagation()}>
                        <h2 className="modal-title" id="eco-discard-title">Discard changes?</h2>
                        <p className="modal-body">Your changes to the schedule haven't been saved.</p>
                        <div className="modal-actions">
                            <button className="btn" onClick={() => setConfirmDiscard(false)}>Keep Editing</button>
                            <button className="btn btn-danger" onClick={discard}>Discard</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PeakManagementPage;
